import { ArrowLeft } from "lucide-react";

import { STUDIO_NAME } from "@domain/studio-app";

import type { ReactNode } from "react";

export interface StudioAppDetailProps {
  /** Display name of the open app (from `readApp`). */
  name: string;
  /** Back to the library (`/studio`). */
  onBack: () => void;
  /** Left pane: the real ChatWindow on the app's thread, or the
   *  "Edit in chat" creator when no thread is bound yet. */
  chatSlot: ReactNode;
  /** Right pane: the live preview (`AppArtifactView`). */
  previewSlot: ReactNode;
}

/**
 * The open-app two-pane state — the mockup's `StudioOpenState` JSX preserved
 * verbatim, panes inverted to slots (studio spec §4.4): the detail route owns
 * the data (app read, thread bind, share state) and passes rendered panes in.
 */
export function StudioAppDetail({ name, onBack, chatSlot, previewSlot }: StudioAppDetailProps) {
  return (
    <div className="flex h-[calc(100vh-10rem)] min-h-[480px] flex-col">
      <div className="mb-4 flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex min-h-[38px] items-center gap-1.5 rounded-full border border-home-ink/15 px-4 font-body text-[13px] font-semibold text-home-ink/70 transition-colors hover:bg-black/5 dark:border-white/15 dark:text-white/70 dark:hover:bg-white/10"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={2.2} />
          {STUDIO_NAME}
        </button>
        <h2 className="min-w-0 truncate font-display text-[18px] font-semibold tracking-tight text-home-ink dark:text-white">
          {name}
        </h2>
      </div>

      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(280px,380px)_1fr]">
        {/* Build chat */}
        <div className="hidden min-h-0 flex-col overflow-hidden rounded-3xl border border-home-ink/8 bg-white lg:flex dark:border-white/8 dark:bg-surface-dark-card">
          {chatSlot}
        </div>

        {/* Live preview */}
        <div className="min-h-0 overflow-hidden rounded-3xl border border-home-ink/8 shadow-[0_16px_40px_-26px_rgba(33,31,28,0.4)] dark:border-white/8">
          {previewSlot}
        </div>
      </div>
    </div>
  );
}
